import React, { useState } from "react";
import { downloadLink } from "../../helpers/constFIles";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import moment from "moment";
import ResumeViewModal from "./ResumeViewModal";
import ViewCandidateDetailsModal from "./ViewCandidateDetailsModal";
import {
    initials, splitSkills, currentRound, decidingRound, joinableRound,
    interviewerOf, nextRoundNumber
} from "./pipeline";
import "../../assets/scss/tables.scss";
import "./CandidateDetails.scss";

/* `accent` is normally one class for the whole list, but the pipeline view
   mixes stages in one grid and passes a function of the candidate instead. */
const accentFor = (accent, data) =>
    (typeof accent === "function" ? accent(data) : accent) || "wh-accent-blue";

const CandidateCard = ({ pool, cardClass, accent = "wh-accent-blue", showInterview, showOutcome,
    showEditBtn, onEdit, onSchedule }) => {

    const [showCandidates, setShowCandidates] = useState(false);
    const [candidate, setCandidate] = useState({})
    const [resumeShow, setResumeShow] = useState(false);
    const [resumeView, setResumeView] = useState({
        filePath: "", filetype: "", resumeName: ""
    })

    const particularCandidate = (candi) => {
        setCandidate({ ...candi })
        setShowCandidates(true);
    }

    const candidatesClose = () => {
        setCandidate({});
        setShowCandidates(false);
    }

    const candidateResume = (data) => {
        setResumeView({
            ...resumeView,
            filePath: downloadLink + data.resumeUrl,
            filetype: data.resumeUrl.split(".").at(-1),
            resumeName: data.name
        })
        setResumeShow(true);
    }

    const resumeHide = () => setResumeShow(false)

    return (
        <div>
            <div className={`wh-cards-grid ${cardClass || ''}`}>
                {pool.map((data, i) => {

                    const skills = splitSkills(data.skills);
                    const round = showInterview ? currentRound(data.interviews) : null;
                    const interviewer = interviewerOf(round);
                    const next = nextRoundNumber(data);

                    const outcome = !showOutcome ? null
                        : data.isSelected ? 'Selected'
                            : data.isRejected ? 'Rejected' : null;
                    const decided = outcome ? decidingRound(data.interviews) : null;

                    return (
                        <article key={i} className={`wh-record-card ${accentFor(accent, data)}`}>

                            <header className="wh-record-head">
                                <div className="wh-avatar">{initials(data.name)}</div>
                                <div className="wh-record-name">
                                    <h6 title={data.name}>{data.name}</h6>
                                    <div className="wh-record-sub">
                                        <span>{data.candidateID}</span>
                                        {outcome && (
                                            <span className={`wh-pill wh-pill-sm ${outcome === 'Selected' ? 'wh-pill-good' : 'wh-pill-danger'}`}>
                                                {outcome}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="wh-record-actions">
                                    <Tooltip title="View candidate" placement="top" arrow>
                                        <IconButton onClick={() => particularCandidate(data)}>
                                            <VisibilityOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {showEditBtn && onEdit && (
                                        <Tooltip title="Edit candidate" placement="top" arrow>
                                            <IconButton onClick={() => onEdit(data)}>
                                                <EditOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </header>

                            <div className="wh-record-role">
                                <WorkOutlineOutlinedIcon />
                                <span title={data.job?.jobTitle}>{data.job?.jobTitle || "No job linked"}</span>
                            </div>

                            <ul className="wh-record-meta">
                                <li>
                                    <MailOutlineRoundedIcon />
                                    <span title={data.email}>{data.email || "--"}</span>
                                </li>
                                <li>
                                    <LocalPhoneOutlinedIcon />
                                    <span title={data.phone}>{data.phone || "--"}</span>
                                </li>
                                <li>
                                    <TimelineOutlinedIcon />
                                    <span>{data.experience ? `${data.experience} experience` : "Experience not set"}</span>
                                </li>
                            </ul>

                            {outcome && (
                                <div className={`wh-outcome ${outcome === 'Selected' ? 'is-good' : 'is-bad'}`}>
                                    {outcome === 'Selected'
                                        ? <TaskAltRoundedIcon />
                                        : <HighlightOffRoundedIcon />}
                                    <div>
                                        <b>{outcome}</b>
                                        <span>
                                            {decided
                                                ? `at Round ${decided.round}${decided.roundType ? ` · ${decided.roundType}` : ''}`
                                                : 'No round recorded'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {round && (
                                <div className="wh-interview">
                                    <div className="wh-interview-top">
                                        <span className="wh-pill wh-pill-good">
                                            Round {round.round} of {data.job?.numberOfRounds || '?'}
                                        </span>
                                        {round.roundType && <span className="wh-pill">{round.roundType}</span>}
                                    </div>

                                    <div className="wh-interview-who">
                                        <span className="wh-avatar wh-avatar-sm">
                                            {initials(interviewer?.employeeName)}
                                        </span>
                                        <div>
                                            <b>{interviewer?.employeeName || 'Interviewer not set'}</b>
                                            <span>{interviewer?.designation || ''}</span>
                                        </div>
                                    </div>

                                    {round.startTime && (
                                        <p className="wh-interview-when">
                                            <EventAvailableOutlinedIcon />
                                            {moment(round.startTime).format('DD MMM YYYY, h:mm A')}
                                        </p>
                                    )}

                                    {round.meetingUrl && (
                                        <a className="iv-join" href={round.meetingUrl}
                                            target="_blank" rel="noreferrer">
                                            <VideocamOutlinedIcon />
                                            Join the call
                                        </a>
                                    )}
                                </div>
                            )}

                            {skills.length > 0 && (
                                <div className="wh-pills">
                                    {skills.slice(0, 4).map((skill, index) =>
                                        <span className="wh-pill" key={index}>{skill}</span>)}
                                    {skills.length > 4 &&
                                        <span className="wh-pill wh-pill-quiet" title={skills.join(", ")}>
                                            +{skills.length - 4}
                                        </span>}
                                </div>
                            )}

                            <footer className="wh-record-foot">
                                <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />}
                                    disabled={!data.resumeUrl}
                                    onClick={() => candidateResume(data)}>
                                    {data.resumeUrl ? "Resume" : "No resume"}
                                </Button>

                                {onSchedule && (
                                    <Button variant="contained" startIcon={<EventRepeatOutlinedIcon />}
                                        onClick={() => onSchedule(data)}>
                                        {next > 1 ? `Schedule round ${next}` : "Schedule interview"}
                                    </Button>
                                )}

                                {/* on a card that is not showing its round, the link
                                    still belongs somewhere reachable */}
                                {!showInterview && joinableRound(data.interviews) && (
                                    <Button variant="outlined" startIcon={<VideocamOutlinedIcon />}
                                        component="a" target="_blank" rel="noreferrer"
                                        href={joinableRound(data.interviews).meetingUrl}>
                                        Join
                                    </Button>
                                )}
                            </footer>

                        </article>
                    )
                })}
            </div>

            <ResumeViewModal resumeData={resumeView} show={resumeShow} onHide={resumeHide} />
            {showCandidates ? <ViewCandidateDetailsModal candidateID={candidate.candidateID} show={showCandidates} onHide={candidatesClose} /> : null}
        </div>
    );
};

export default CandidateCard;
